import { apiFetch } from './ultra-meet-config';

// Complete application configuration interface
interface AppConfig {
    // OIDC Configuration
    oidcEnabled: boolean;
    oidcIssuer?: string;
    oidcClientId?: string;
    oidcRedirectUri?: string;
    oidcMobileClientId?: string;
    oidcMobileRedirectUri?: string;

    // Media Configuration
    requireMediaDevices: boolean;

    // Chat Configuration
    isChatWithFile: boolean;
    chatPersistence: boolean;

    // Feature Flags
    roomRecording: boolean;
    meetingNotes: boolean;
    sttEnabled: boolean;
    emailRegistrationEnabled: boolean;
    noiseSuppression: boolean;
    guestAccessEnabled: boolean;
}

interface AuthConfigResponse {
    result: string;
    config: AppConfig;
}

// Shared cache for all application configuration
let cachedAppConfig: AppConfig | null = null;

// Single API call to fetch all configuration
const fetchAppConfig = async (): Promise<AppConfig> => {
    if (cachedAppConfig) {
        return cachedAppConfig;
    }

    try {
        const response = await apiFetch(`/api/auth-config`);

        if (!response.ok) {
            throw new Error(`Failed to fetch auth config: ${response.status}`);
        }

        const data: AuthConfigResponse = await response.json();

        if (data.result !== 'success') {
            throw new Error('Invalid response from auth-config endpoint');
        }

        cachedAppConfig = data.config;
        return cachedAppConfig;
    } catch (error) {
        console.error('Failed to fetch app config, falling back to environment variables:', error);

        // Fallback to safe defaults if API call fails
        const fallbackConfig: AppConfig = {
            oidcEnabled: false,
            oidcIssuer: undefined,
            oidcClientId: undefined,
            oidcRedirectUri: undefined,
            requireMediaDevices: false,
            isChatWithFile: false,
            chatPersistence: false,
            roomRecording: false,
            meetingNotes: false,
            sttEnabled: false,
            emailRegistrationEnabled: true,
            noiseSuppression: false,
            guestAccessEnabled: true
        };

        cachedAppConfig = fallbackConfig;
        return fallbackConfig;
    }
};

// Get OIDC-specific configuration
const getOidcConfig = async () => {
    const config = await fetchAppConfig();
    return {
        oidcEnabled: config.oidcEnabled,
        oidcIssuer: config.oidcIssuer,
        oidcClientId: config.oidcClientId,
        oidcRedirectUri: config.oidcRedirectUri,
        oidcMobileClientId: config.oidcMobileClientId,
        oidcMobileRedirectUri: config.oidcMobileRedirectUri
    };
};

// Get media-specific configuration
const getMediaConfig = async () => {
    const config = await fetchAppConfig();
    return {
        requireMediaDevices: config.requireMediaDevices
    };
};

// Get chat-specific configuration
const getChatConfig = async () => {
    const config = await fetchAppConfig();
    return {
        isChatWithFile: config.isChatWithFile,
        chatPersistence: config.chatPersistence
    };
};

// Get feature configuration
const getFeatureConfig = async () => {
    const config = await fetchAppConfig();
    return {
        roomRecording: config.roomRecording,
        meetingNotes: config.meetingNotes,
        sttEnabled: config.sttEnabled,
        noiseSuppression: config.noiseSuppression
    };
};

// Helper functions for specific config checks
const isOidcEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.oidcEnabled;
    } catch (error) {
        console.error('Failed to check OIDC status:', error);
        return false;
    }
};

const isMediaDevicesRequired = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.requireMediaDevices;
    } catch (error) {
        console.error('Failed to check media devices requirement:', error);
        return false;
    }
};

const isRoomRecordingEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.roomRecording;
    } catch (error) {
        console.error('Failed to check room recording availability:', error);
        return false;
    }
};

const isMeetingNotesEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.meetingNotes;
    } catch (error) {
        console.error('Failed to check meeting notes availability:', error);
        return false;
    }
};

const isNoiseSuppressionFeatureEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.noiseSuppression;
    } catch (error) {
        console.error('Failed to check noise suppression availability:', error);
        return false;
    }
};

const isSTTEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.sttEnabled;
    } catch (error) {
        console.error('Failed to check STT availability:', error);
        return false;
    }
};

const isGuestAccessEnabled = async (): Promise<boolean> => {
    try {
        const config = await fetchAppConfig();
        return config.guestAccessEnabled;
    } catch (error) {
        console.error('Failed to check guest access availability:', error);
        return true; // fail-open: сохранить дефолтное поведение
    }
};

export {
    fetchAppConfig,
    getOidcConfig,
    getMediaConfig,
    getChatConfig,
    getFeatureConfig,
    isOidcEnabled,
    isMediaDevicesRequired,
    isRoomRecordingEnabled,
    isMeetingNotesEnabled,
    isNoiseSuppressionFeatureEnabled,
    isSTTEnabled,
    isGuestAccessEnabled,
    type AppConfig
};
